/**
 * Admin Google Workspace API
 *
 * Covers:
 *   - GW integration config (get / set / delete)
 *   - GW group registration
 *   - GW group sync trigger
 *
 * Config routes live under /admin/google-workspace/config.
 * Group routes live under /group/google-workspace/register and /group/{id}/sync.
 */

import api from "./index";
import ENDPOINTS from "./endpoints";
import logger from "../services/Logger";

// ─── Config ───────────────────────────────────────────────────────────────────

/**
 * Get the Google Workspace integration config for the org.
 * Returns null if not configured (204).
 * @returns {Promise<Object|null>}
 */
export const getGoogleWorkspaceConfig = async () => {
  try {
    const response = await api({ method: "GET", url: ENDPOINTS.ADMIN_GW_CONFIG });
    if (response.status === 204) return null;
    return response.data;
  } catch (error) {
    logger.error("adminGoogleWorkspace", "Error getting GW config", { error: error.message });
    throw error;
  }
};

/**
 * Save / replace the Google Workspace integration config.
 * @param {string} domain  e.g. "acme.com"
 * @returns {Promise<{configured: boolean}>}
 */
export const setGoogleWorkspaceConfig = async (domain) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_GW_CONFIG,
      data: { domain },
    });
    return response.data;
  } catch (error) {
    logger.error("adminGoogleWorkspace", "Error setting GW config", { error: error.message });
    throw error;
  }
};

/**
 * Delete the Google Workspace integration config (disables integration).
 * @returns {Promise<{deleted: boolean}>}
 */
export const deleteGoogleWorkspaceConfig = async () => {
  try {
    const response = await api({ method: "DELETE", url: ENDPOINTS.ADMIN_GW_CONFIG });
    return response.data;
  } catch (error) {
    logger.error("adminGoogleWorkspace", "Error deleting GW config", { error: error.message });
    throw error;
  }
};

// ─── Group registration ───────────────────────────────────────────────────────

/**
 * Register a Google Workspace group in Photon.
 * @param {string} groupEmail   e.g. "eng@acme.com"
 * @returns {Promise<{group_id: string}>}
 */
export const registerGoogleWorkspaceGroup = async (groupEmail) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_GW_REGISTER_GROUP,
      data: { group_email: groupEmail },
    });
    return response.data;
  } catch (error) {
    logger.error("adminGoogleWorkspace", "Error registering GW group", { error: error.message, groupEmail });
    throw error;
  }
};

// ─── Sync ─────────────────────────────────────────────────────────────────────

/**
 * Trigger a manual membership refresh for a Google Workspace group.
 * @param {string} groupId
 * @returns {Promise<{added: number, removed: number, group_id: string}>}
 */
export const syncGoogleWorkspaceGroup = async (groupId) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_GW_SYNC_GROUP(groupId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminGoogleWorkspace", "Error syncing GW group", { error: error.message, groupId });
    throw error;
  }
};
